import { Authenticated, Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router";

import { MutationCache, QueryCache } from "@tanstack/react-query";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppLayout } from "@/components/layout/AppLayout";
import { reportQueryError } from "@/lib/errors";
import { authProvider, dataProvider } from "@/lib/providers";
import { ActivityPage } from "@/pages/ActivityPage";
import { AnalysisPage } from "@/pages/AnalysisPage";
import { AppointmentsPage } from "@/pages/AppointmentsPage";
import { CabinetPage } from "@/pages/CabinetPage";
import { BitrixChatsPage } from "@/pages/BitrixChatsPage";
import { MessengerChatPage } from "@/pages/MessengerChatPage";
import { MessengerPage } from "@/pages/MessengerPage";
import { BitrixUsersPage } from "@/pages/BitrixUsersPage";
import { ChatStatsPage } from "@/pages/ChatStatsPage";
import { KeywordsPage } from "@/pages/KeywordsPage";
import { QcPage } from "@/pages/QcPage";
import { ChatsPage } from "@/pages/ChatsPage";
import KnowledgePage from "@/pages/KnowledgePage";
import { CallsPage } from "@/pages/CallsPage";
import { CallCardPage } from "@/pages/CallCardPage";
import { CallPhonePage } from "@/pages/CallPhonePage";
import { DialingDayPage } from "@/pages/DialingDays";
import { DialingPage } from "@/pages/DialingPage";
import { DealCardPage } from "@/pages/DealCardPage";
import { LeadCardPage } from "@/pages/LeadCardPage";
import { DistributionPage } from "@/pages/DistributionPage";
import { ExtensionPage } from "@/pages/ExtensionPage";
import { HomePage } from "@/pages/HomePage";
import { BonusPage } from "@/pages/BonusPage";
import { IdlePage } from "@/pages/IdlePage";
import { MetrikaPage } from "@/pages/MetrikaPage";
import { SalaryPage } from "@/pages/SalaryPage";
import { SalonPage } from "@/pages/SalonPage";
import { IdleUserPage } from "@/pages/IdleUserPage";
import { LeadsPage } from "@/pages/LeadsPage";
import { MailingPage } from "@/pages/MailingPage";
import { LoginPage } from "@/pages/LoginPage";
import { PodborAnswerPage } from "@/pages/PodborAnswerPage";
import { PodborHomePage } from "@/pages/PodborHomePage";
import { PermissionsPage } from "@/pages/PermissionsPage";
import { SchedulePage } from "@/pages/SchedulePage";
import { ComplaintsPage } from "@/pages/ComplaintsPage";
import { DeliveryPage } from "@/pages/DeliveryPage";
import { NonreturnPage } from "@/pages/NonreturnPage";
import { VideoPage } from "@/pages/VideoPage";
import { TelephonyPage } from "@/pages/TelephonyPage";
import { VoiceLabPage } from "@/pages/VoiceLabPage";
import { BotsPage } from "@/pages/BotsPage";
import { IntegrationsPage } from "@/pages/IntegrationsPage";
import { RequestsPage } from "@/pages/RequestsPage";
import { SearchPage } from "@/pages/SearchPage";
import { ShiftPage } from "@/pages/ShiftPage";
import { TelegramPage } from "@/pages/TelegramPage";
import { TemplatesPage } from "@/pages/TemplatesPage";

/**
 * На вход — с адресом, куда шёл: `?to=` читает `useLogin` и после пароля
 * возвращает туда. Ссылку на смену из Telegram открывают незалогиненные, и
 * без этого они оказывались бы в общем списке, а не на нужном сотруднике.
 */
function ToLogin() {
  const { pathname, search } = useLocation();
  const to =
    pathname === "/" ? "" : `?to=${encodeURIComponent(pathname + search)}`;
  return <Navigate to={`/login${to}`} replace />;
}

/** Упавший экран раздела не гасит меню кабинета; смена адреса — новая попытка. */
function RouteBoundary() {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname}>
      <Outlet />
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        routerProvider={routerProvider}
        options={{
          disableTelemetry: true,
          // Окончательный отказ запроса (после повторов) — в журнал ошибок; что видел сервер, он записал сам.
          reactQuery: {
            clientConfig: {
              // Refine держит всем запросам прежние данные: при смене ключа `data` — ответ про прошлый
              // номер или сделку, и сотрудник действовал над чужим объектом (30.10.26: новый номер со
              // сделкой прошлого звонка). Кому это нужно, задаёт `keepPreviousData` у своего запроса.
              defaultOptions: { queries: { placeholderData: undefined } },
              queryCache: new QueryCache({ onError: reportQueryError }),
              mutationCache: new MutationCache({ onError: reportQueryError }),
            },
          },
        }}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* «Подбор» — без входа: главная (поиск по серии и фото, её держат в закладках) и ответ по хэшу. */}
          <Route path="/podbor" element={<PodborHomePage />} />
          <Route path="/podbor/:token" element={<PodborAnswerPage />} />
          {/* Лендинг привязки: сюда ведут из бота люди, которые кабинетом не пользуются, — без меню. */}
          <Route
            path="/telegram"
            element={
              <Authenticated key="telegram" fallback={<ToLogin />}>
                <TelegramPage />
              </Authenticated>
            }
          />
          {/* «Чаты» — на весь экран, как Telegram Web: с телефона меню кабинета съело бы ширину. */}
          {["/chats", "/chats/:chatId"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <Authenticated key="chats" fallback={<ToLogin />}>
                  <ChatsPage />
                </Authenticated>
              }
            />
          ))}
          {/* Прежние адреса чата Telegram (до общего мессенджера) — в общий список. */}
          <Route path="/chats/telegram/*" element={<Navigate to="/chats" replace />} />
          <Route
            element={
              /* Незалогиненный не должен видеть каркас кабинета даже мгновение. */
              <Authenticated key="app" fallback={<ToLogin />}>
                <AppLayout>
                  <RouteBoundary />
                </AppLayout>
              </Authenticated>
            }
          >
            <Route index element={<HomePage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/leads/:leadId" element={<LeadCardPage />} />
            <Route path="/distribution" element={<DistributionPage />} />
            <Route path="/mailing" element={<MailingPage />} />
            <Route path="/appointments" element={<AppointmentsPage />} />
            <Route path="/cabinet" element={<CabinetPage />} />
            <Route path="/site" element={<MetrikaPage />} />
            <Route path="/analysis" element={<AnalysisPage />} />
            <Route path="/analysis/:slug" element={<AnalysisPage />} />
            <Route path="/bitrix-users" element={<BitrixUsersPage />} />
            <Route path="/calls" element={<CallsPage />} />
            <Route path="/calls/:callId" element={<CallCardPage />} />
            <Route path="/calls/phone/:phone" element={<CallPhonePage />} />
            <Route path="/bitrix-chats" element={<BitrixChatsPage />} />
            <Route path="/messenger" element={<MessengerPage />} />
            <Route
              path="/messenger/chat/:chatId"
              element={<MessengerChatPage />}
            />
            <Route path="/dialing" element={<DialingPage />} />
            <Route path="/dialing/day/:day" element={<DialingDayPage />} />
            <Route path="/deals" element={<DealCardPage />} />
            <Route path="/deals/:dealId" element={<DealCardPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/activity/:userId" element={<ShiftPage />} />
            <Route path="/idle" element={<IdlePage />} />
            <Route path="/salon" element={<SalonPage />} />
            <Route path="/idle/:userId" element={<IdleUserPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/complaints" element={<ComplaintsPage />} />
            <Route path="/delivery" element={<DeliveryPage />} />
            <Route path="/nonreturn" element={<NonreturnPage />} />
            <Route path="/video" element={<VideoPage />} />
            <Route path="/telephony" element={<TelephonyPage />} />
            <Route path="/voice-lab" element={<VoiceLabPage />} />
            <Route path="/bots" element={<BotsPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/bonus" element={<BonusPage />} />
            <Route path="/salary" element={<SalaryPage />} />
            <Route path="/chat-stats" element={<ChatStatsPage />} />
            <Route path="/keywords" element={<KeywordsPage />} />
            <Route path="/qc" element={<QcPage />} />
            {/* «База знаний» — в каркасе с меню, как остальные разделы (с 26.10.26). */}
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/knowledge/q/:questionId" element={<KnowledgePage />} />
            <Route path="/knowledge/:cardId" element={<KnowledgePage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/extension" element={<ExtensionPage />} />
            <Route path="/settings/permissions" element={<PermissionsPage />} />
          </Route>
        </Routes>
      </Refine>
    </BrowserRouter>
  );
}
